import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Paper, Pagination, useMediaQuery } from "@mui/material";

const GitLogs = () => {
    const isMobile = useMediaQuery("(max-width: 600px)");
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const logsPerPage = 100;

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/git-logs`);
                const data = await response.json();

                if (data && data.logs && Array.isArray(data.logs)) {

                    const sortedLogs = [...data.logs].sort((a, b) =>
                        a.commit_number - b.commit_number || new Date(a.timestamp) - new Date(b.timestamp)
                    );

                    const totalCommits = sortedLogs.length;
                    sortedLogs.forEach((log, index) => {
                        log.commit_number = totalCommits - index;
                    });

                    setLogs(sortedLogs);
                } else {
                    setLogs([]);
                }
            } catch (error) {
                setLogs([]);
            }
        };

        fetchLogs();
    }, []);

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const startIndex = (page - 1) * logsPerPage;
    const endIndex = startIndex + logsPerPage;
    const paginatedLogs = logs.slice(startIndex, endIndex);

    return (
        <Box sx={{ maxWidth: 950, margin: "auto", py: 4, position: "relative", backgroundColor: "#111111" }}>
            {!logs ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress sx={{ color: "#98FF98" }} />
                </Box>
            ) : (
                <Box sx={{ mb: "12px" }}>
                    <Paper sx={{ padding: 2, borderRadius: "10px" }}>
                        {paginatedLogs.length > 0 ? (
                            paginatedLogs.map((log, index) => (
                                <Box
                                    key={log.id}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        marginBottom: "20px",
                                        padding: "12px",
                                        backgroundColor: "#1a1a1a",
                                        boxShadow: "0px 4px 15px rgba(30, 30, 30, 0.4), inset 0px 2px 6px rgba(115, 115, 115, 0.2)",
                                        borderRadius: "10px",
                                        overflowX: "auto",
                                        whiteSpace: isMobile ? "none" : "nowrap"
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: !isMobile ? "none" : "",
                                            fontSize: isMobile ? "12px" : "14px",
                                            fontWeight: "bold",
                                            marginRight: "4px",
                                            mb: "2px",
                                            pt: "4px",
                                            px: "2px",
                                            backgroundColor: "#212121",
                                            color: "#98FF98",
                                            borderRadius: "12px",
                                            minWidth: "40px",
                                            textAlign: "center",
                                        }}
                                    >
                                        #{log.commit_number}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: isMobile ? "none" : "",
                                                fontSize: isMobile ? "12px" : "14px",
                                                fontWeight: "bold",
                                                marginRight: "4px",
                                                mb: "2px",
                                                px: "4px",
                                                pt: "2px",
                                                backgroundColor: "#232323",
                                                color: "#98FF98",
                                                borderRadius: "12px",
                                                minWidth: "45px",
                                                textAlign: "center",
                                            }}
                                        >
                                            #{log.commit_number}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontSize: isMobile ? "12px" : "16px", color: "#eeeeee" }}>
                                            {log.message}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: "#bfbfbf", mt: 0.5 }}>
                                        {log.author}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#bfbfbf" }}>
                                        {log.timestamp}
                                    </Typography>

                                    <Box sx={{ display: "flex", mt: 1 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                                padding: "4px 6px",
                                                backgroundColor: "#005f00",
                                                color: "#98FF98",
                                                borderRadius: "4px",
                                                marginRight: "8px",
                                            }}
                                        >
                                            + {log.additions} lines
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                                padding: "4px 6px",
                                                backgroundColor: "#5f0000",
                                                color: "#ff9898",
                                                borderRadius: "4px",
                                            }}
                                        >
                                            - {log.deletions} lines
                                        </Typography>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2">No logs found.</Typography>
                        )}
                    </Paper>

                    {logs.length > logsPerPage && (
                        <Box
                            sx={{
                                width: isMobile ? "92%" : "auto",
                                position: "fixed",
                                bottom: 16,
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "#1d1d1d",
                                borderRadius: "8px",
                                padding: "8px 16px",
                                boxShadow: "0px 0px 4px gray",
                                zIndex: 1000,
                            }}
                        >
                            <Pagination
                                count={Math.ceil(logs.length / logsPerPage)}
                                page={page}
                                onChange={handlePageChange}
                                sx={{
                                    "& .MuiPaginationItem-root": {
                                        color: "#cccccc",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    },
                                    "& .Mui-selected": {
                                        color: "#98FF98",
                                        fontWeight: "bold"
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default GitLogs;
